// Menús cíclicos rotativos (p.ej. 14 al año): grilla 7 días × servicios, costeada en vivo.
// Catering → Operación → Menús cíclicos.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarRange, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import {
  Ciclo, CicloItem, DIAS, RecetaResumen, SERVICIOS, SERVICIO_LABEL,
  cat, catRpc, invalidateCatering, money,
} from './shared';

interface MenuCicloResp { ciclo_id: string; nombre: string; items: CicloItem[] }

export function MenusCatering() {
  const qc = useQueryClient();
  const [cicloId, setCicloId] = useState('');
  const [nuevoCiclo, setNuevoCiclo] = useState('');
  const [celda, setCelda] = useState<{ dia: number; servicio: string } | null>(null);
  const [addReceta, setAddReceta] = useState('');
  const invalidar = () => invalidateCatering(qc);

  const { data: ciclos = [] } = useQuery<Ciclo[]>({
    queryKey: ['cat-ciclos'],
    queryFn: () => cat.list<Ciclo>('ciclos', '?select=id,nombre,orden,es_activo&deleted_at=is.null&order=orden,nombre'),
  });
  const cicloSel = cicloId || ciclos[0]?.id || '';

  const { data: menu } = useQuery<MenuCicloResp>({
    queryKey: ['cat-menu-ciclo', cicloSel], enabled: !!cicloSel,
    queryFn: () => catRpc<MenuCicloResp>('menu_ciclo', { _ciclo_id: cicloSel }),
  });
  const { data: recetas = [] } = useQuery<RecetaResumen[]>({
    queryKey: ['cat-recetas'],
    queryFn: () => catRpc<RecetaResumen[]>('recetas_listar', { _tipo: null }),
  });

  const crearCiclo = useMutation({
    mutationFn: () => cat.create('ciclos', { nombre: nuevoCiclo, orden: ciclos.length + 1 }),
    onSuccess: () => { toast.success('Menú creado'); setNuevoCiclo(''); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const agregarItem = useMutation({
    mutationFn: () => cat.create('ciclo_menus', {
      ciclo_id: cicloSel, dia: celda!.dia, servicio: celda!.servicio, receta_id: addReceta,
      orden: (menu?.items.filter((i) => i.dia === celda!.dia && i.servicio === celda!.servicio).length ?? 0) + 1,
    }),
    onSuccess: () => { toast.success('Plato agregado al menú'); setCelda(null); setAddReceta(''); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitarItem = useMutation({
    mutationFn: (id: string) => cat.remove('ciclo_menus', id),
    onSuccess: () => { toast.success('Plato quitado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const servicios = useMemo(() => {
    const activos = new Set(menu?.items.map((i) => i.servicio) ?? []);
    return SERVICIOS.filter((s) => activos.has(s) || s === 'desayuno' || s === 'almuerzo_1' || s === 'merienda');
  }, [menu]);

  const itemsDe = (dia: number, servicio: string) =>
    (menu?.items ?? []).filter((i) => i.dia === dia && i.servicio === servicio);

  const costoDia = (dia: number) =>
    (menu?.items ?? []).filter((i) => i.dia === dia).reduce((s, i) => s + Number(i.costo_porcion || 0), 0);

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><CalendarRange className="h-4 w-4" /> Menús cíclicos</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-2">
            <select className={`${fieldCls} w-56`} value={cicloSel} onChange={(e) => setCicloId(e.target.value)}>
              {ciclos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
            <div className="flex gap-2">
              <input className={`${fieldCls} w-44`} placeholder="Nuevo menú (p.ej. Menú 2)" value={nuevoCiclo} onChange={(e) => setNuevoCiclo(e.target.value)} />
              <Button size="sm" variant="outline" disabled={!nuevoCiclo || crearCiclo.isPending} onClick={() => crearCiclo.mutate()}><Plus className="h-4 w-4" /></Button>
            </div>
            <p className="ml-auto text-sm text-muted-foreground">
              La biblioteca de menús rota durante el año (p.ej. 14 menús ≈ ciclo de 14 semanas).
            </p>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="w-28 py-2 pr-2">Servicio</th>
                {DIAS.slice(0, 6).map((d) => <th key={d} className="py-2 pr-2">{d}</th>)}
              </tr>
            </thead>
            <tbody>
              {servicios.map((srv) => (
                <tr key={srv} className="border-b align-top last:border-0">
                  <td className="py-2 pr-2 font-medium">{SERVICIO_LABEL[srv]}</td>
                  {DIAS.slice(0, 6).map((_, di) => {
                    const dia = di + 1;
                    const items = itemsDe(dia, srv);
                    return (
                      <td key={dia} className="py-2 pr-2">
                        <div className="space-y-1">
                          {items.map((i) => (
                            <div key={i.id} className="group flex items-center justify-between gap-1 rounded-md border bg-card px-2 py-1">
                              <span className="text-xs">
                                {i.nombre}
                                <span className="ml-1 font-mono text-muted-foreground">{money(i.costo_porcion, 3)}</span>
                                {i.sin_costo > 0 && <AlertTriangle className="ml-1 inline h-3 w-3 text-amber-500" />}
                              </span>
                              <button className="opacity-0 group-hover:opacity-100" onClick={() => quitarItem.mutate(i.id)}>
                                <Trash2 className="h-3 w-3 text-red-500" />
                              </button>
                            </div>
                          ))}
                          {celda?.dia === dia && celda?.servicio === srv ? (
                            <div className="flex gap-1">
                              <select className={`${fieldCls} text-xs`} value={addReceta} onChange={(e) => setAddReceta(e.target.value)}>
                                <option value="">Receta…</option>
                                {recetas.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                              </select>
                              <Button size="sm" disabled={!addReceta || agregarItem.isPending} onClick={() => agregarItem.mutate()}><Plus className="h-3 w-3" /></Button>
                            </div>
                          ) : (
                            <button className="w-full rounded-md border border-dashed px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
                              onClick={() => { setCelda({ dia, servicio: srv }); setAddReceta(''); }}>
                              + agregar
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="font-medium">
                <td className="py-2 pr-2">Costo del día (Σ por porción)</td>
                {DIAS.slice(0, 6).map((_, di) => (
                  <td key={di} className="py-2 pr-2 font-mono">{money(costoDia(di + 1), 3)}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <p className="pt-2 text-xs text-muted-foreground">
            {num((menu?.items ?? []).length, 0)} ítems en el menú · celdas con <AlertTriangle className="inline h-3 w-3 text-amber-500" /> tienen ingredientes sin precio (costo incompleto)
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
